import { SUBSCRIPTION_RUNTIME_TYPES, type AgentDetail } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { AlertTriangle, BookOpen, Bot, ChevronLeft, Cpu, Crown, FileText, History, ListTodo, Pencil, Power, ShieldCheck, SquareStack, Trash2 } from 'lucide-react';
import { AgentDialog } from '@/components/agent-dialog';
import { DeleteAgentButton } from '@/components/delete-agent-button';
import { JsonBlock } from '@/components/code-block';
import { Markdown } from '@/components/markdown';
import { SubscriptionStatus } from '@/components/runtime-login-dialog';
import { AgentSkillsDialog, SkillSourceBadge } from '@/components/skill-dialogs';
import { DefinitionList, Page, PageHeader } from '@/components/page';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { AgentStatusBadge, ExecutionStatusBadge } from '@/components/status-badge';
import { EmptyState, ErrorState, LoadingPage } from '@/components/states';
import { TechDetails } from '@/components/tech-details';
import { WorkItemList } from '@/components/work-item-row';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { isEmptyObject } from '@/lib/format';
import { t, tr } from '@/lib/i18n';
import { useApiMutation } from '@/lib/hooks';
import { RESOURCE_CLASS, RUNTIME_TYPE } from '@/lib/labels';
import { qk } from '@/lib/query-keys';
import { runtimeReady } from '@/routes/start';

export function AgentDetailPage() {
  const { workspaceId, agentId } = useParams({ from: '/_authed/w/$workspaceId/agents/$agentId' });
  const agent = useQuery({ queryKey: qk.agent(agentId), queryFn: () => api.agents.get(agentId) });

  if (agent.isPending) return <LoadingPage />;
  if (agent.isError) {
    return (
      <Page>
        <BackLink workspaceId={workspaceId} />
        <ErrorState error={agent.error} onRetry={() => void agent.refetch()} />
      </Page>
    );
  }
  return <AgentView agent={agent.data} workspaceId={workspaceId} />;
}

function BackLink({ workspaceId }: { workspaceId: string }) {
  return (
    <Link
      to="/w/$workspaceId/agents"
      params={{ workspaceId }}
      className="mb-4 inline-flex items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground"
    >
      <ChevronLeft className="size-4" /> {t('All agents')}
    </Link>
  );
}

function AgentView({ agent, workspaceId }: { agent: AgentDetail; workspaceId: string }) {
  const toggle = useApiMutation(
    () => api.agents.update(agent.id, { status: agent.status === 'active' ? 'disabled' : 'active' }),
    {
      invalidate: [qk.agent(agent.id), qk.agents(workspaceId)],
      success: (a) => (a.status === 'active' ? t('Agent enabled') : t('Agent disabled')),
    },
  );
  const navigate = useNavigate();
  const profile = agent.runtimeProfile;

  return (
    <Page wide>
      <BackLink workspaceId={workspaceId} />
      <PageHeader
        actionsOnTop
        title={
          <span className="inline-flex flex-wrap items-center gap-3">
            {agent.name}
            <AgentStatusBadge status={agent.status} />
          </span>
        }
        description={agent.description || t('No description.')}
        actions={
          <>
            <AgentDialog workspaceId={workspaceId} agent={agent}>
              <Button variant="outline" size="sm">
                <Pencil /> {t('Edit')}
              </Button>
            </AgentDialog>
            <Button variant="outline" size="sm" onClick={() => toggle.mutate()} loading={toggle.isPending}>
              <Power /> {agent.status === 'active' ? t('Disable') : t('Enable')}
            </Button>
            <DeleteAgentButton
              agent={agent}
              workspaceId={workspaceId}
              onDeleted={() => void navigate({ to: '/w/$workspaceId/agents', params: { workspaceId } })}
            >
              <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                <Trash2 /> {t('Delete')}
              </Button>
            </DeleteAgentButton>
          </>
        }
      />

      {profile && !runtimeReady(profile) && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          {tr('This agent runs on {profile}, which is not connected yet, so it can’t work. Connect it under Settings → Runtimes, or give the agent a connected runtime (Edit).', {
            profile: <span className="font-medium">{profile.name}</span>,
          })}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-6 lg:col-span-2">
          <Section icon={<FileText />} title={t('Instructions')} description={t('How this agent works.')}>
            {agent.instructions.trim() ? (
              <div className="max-h-[32rem] overflow-y-auto">
                <Markdown text={agent.instructions} />
              </div>
            ) : (
              <EmptyState compact title={t('No instructions')} description={t('Without instructions, the agent works only with the desk’s playbook.')} />
            )}
          </Section>

          <Section icon={<ListTodo />} title={t('Working on now')}>
            {agent.currentWorkItems.length === 0 ? (
              <EmptyState compact title={t('Nothing right now')} description={t('This agent isn’t working on a work item at the moment.')} />
            ) : (
              <WorkItemList items={agent.currentWorkItems} workspaceId={workspaceId} />
            )}
          </Section>

          <Section icon={<History />} title={t('Recently worked on')}>
            {agent.recentWorkItems.length === 0 ? (
              <EmptyState compact title={t('No work yet')} />
            ) : (
              <WorkItemList items={agent.recentWorkItems} workspaceId={workspaceId} />
            )}
          </Section>

          <Section icon={<AlertTriangle />} title={t('Recent errors')} description={t('Runs that went wrong.')}>
            {agent.recentFailures.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('No errors recently.')}</p>
            ) : (
              <ul className="-mx-5 -mb-5 divide-y border-t">
                {agent.recentFailures.map((ex) => (
                  <li key={ex.id} className="flex items-start gap-3 px-5 py-2.5">
                    <ExecutionStatusBadge status={ex.status} />
                    <div className="min-w-0 flex-1">
                      <div className="line-clamp-2 font-mono text-xs break-words">{ex.error || t('Ended without an error message')}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        <RelativeTime value={ex.finishedAt ?? ex.createdAt} />
                        {ex.workItemId && (
                          <>
                            {' · '}
                            <Link
                              to="/w/$workspaceId/work-items/$workItemId"
                              params={{ workspaceId, workItemId: ex.workItemId }}
                              className="hover:text-foreground hover:underline"
                            >
                              {t('Open work item')}
                            </Link>
                          </>
                        )}
                        {ex.kind === 'desk_evaluation' && ` · ${t('Lead review')}`}
                        {ex.kind === 'lead' && ` · ${t('Lead run')}`}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        <div className="flex min-w-0 flex-col gap-6">
          <Section icon={<Cpu />} title={t('Runtime')} description={t('Which AI provider and model the agent uses.')}>
            {profile ? (
              <DefinitionList
                className="text-[13px]"
                items={[
                  {
                    label: t('Profile'),
                    value: agent.runtimeAutomatic ? (
                      <span className="inline-flex flex-wrap items-center gap-1.5">
                        {t('Automatic')} <span className="text-muted-foreground">{t('→ {name} (next run)', { name: profile.name })}</span>
                      </span>
                    ) : (
                      profile.name
                    ),
                  },
                  {
                    label: t('Provider'),
                    value: (
                      <span className="inline-flex flex-wrap items-center gap-1.5">
                        {RUNTIME_TYPE[profile.runtimeType] ?? profile.runtimeType}
                        {!profile.implemented && <Badge tone="amber">{t('not supported yet')}</Badge>}
                      </span>
                    ),
                  },
                  {
                    label: t('Model'),
                    value:
                      profile.runtimeType === 'local' ? (
                        '—'
                      ) : (
                        <span className="inline-flex flex-wrap items-center gap-1.5">
                          <span className="font-mono">{agent.effectiveModel ?? '—'}</span>
                          {!agent.model && <span className="text-xs text-muted-foreground">{t('(runtime default)')}</span>}
                        </span>
                      ),
                  },
                  { label: t('Resources'), value: RESOURCE_CLASS[profile.resourceClass]?.label ?? profile.resourceClass },
                  {
                    label: t('Credentials'),
                    value: SUBSCRIPTION_RUNTIME_TYPES.includes(profile.runtimeType) ? (
                      <SubscriptionStatus profile={profile} />
                    ) : profile.runtimeType === 'local' ? (
                      t('not needed')
                    ) : profile.hasCredential ? (
                      t('API key saved')
                    ) : (
                      <span className="text-amber-700 dark:text-amber-400">{t('No API key')}</span>
                    ),
                  },
                ]}
              />
            ) : (
              <p className="text-sm text-amber-700 dark:text-amber-400">
                {agent.runtimeAutomatic
                  ? t('Automatic, but no runtime is available — connect one under Settings → Runtimes, or wait until a used-up plan resets.')
                  : t('No runtime selected — the agent can’t work like this. Choose one via “Edit”.')}
              </p>
            )}
          </Section>

          <Section icon={<SquareStack />} title={t('Desks')} description={t('Where this agent is in use.')}>
            {agent.desks.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('Not assigned to a desk yet. Add it on a desk’s page.')}</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {agent.desks.map((d) => (
                  <li key={d.id}>
                    <Link
                      to="/w/$workspaceId/desks/$deskId"
                      params={{ workspaceId, deskId: d.id }}
                      className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                    >
                      {d.role === 'lead' ? <Crown className="size-3.5 text-primary" /> : <Bot className="size-3.5 text-muted-foreground" />}
                      <span className="flex-1 truncate">{d.name}</span>
                      {d.role === 'lead' ? <Badge tone="accent">{t('Lead')}</Badge> : <Badge tone="outline">{t('Available')}</Badge>}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section
            icon={<BookOpen />}
            title={t('Skills')}
            description={t('The agent loads a skill when a task needs it.')}
            actions={
              <AgentSkillsDialog workspaceId={workspaceId} agentId={agent.id} enabled={agent.skills}>
                <Button variant="outline" size="sm">
                  {t('Choose')}
                </Button>
              </AgentSkillsDialog>
            }
          >
            {agent.skills.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('No skills yet.')}</p>
            ) : (
              <ul className="-mx-2 flex flex-col gap-0.5">
                {agent.skills.map((s) => (
                  <li key={s.id}>
                    <Link
                      to="/w/$workspaceId/skills/$skillId"
                      params={{ workspaceId, skillId: s.id }}
                      className="block rounded-md px-2 py-1.5 hover:bg-muted"
                    >
                      <span className="flex flex-wrap items-center gap-1.5">
                        <span className="font-mono text-[13px] font-medium">{s.name}</span>
                        <SkillSourceBadge skill={s} />
                        {s.status === 'disabled' && <Badge tone="amber">{t('disabled')}</Badge>}
                        {s.status === 'retired' && <Badge tone="muted">{t('no longer available')}</Badge>}
                      </span>
                      <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">{s.description}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section icon={<ShieldCheck />} title={t('Capabilities & rules')}>
            <div className="grid gap-3">
              <div>
                <div className="mb-1.5 text-xs font-medium text-muted-foreground">{t('Capabilities')}</div>
                {agent.capabilities.length ? (
                  <div className="flex flex-wrap gap-1">
                    {agent.capabilities.map((c) => (
                      <Badge key={c} tone="outline">
                        {c}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-[13px] text-muted-foreground">{t('None specified')}</p>
                )}
              </div>
              <div>
                <div className="mb-1.5 text-xs font-medium text-muted-foreground">{t('Tools (tool policy)')}</div>
                {isEmptyObject(agent.toolPolicy) ? (
                  <p className="text-[13px] text-muted-foreground">{t('Default')}</p>
                ) : (
                  <JsonBlock value={agent.toolPolicy} maxHeight="max-h-48" />
                )}
              </div>
              <div>
                <div className="mb-1.5 text-xs font-medium text-muted-foreground">{t('Permissions (permission policy)')}</div>
                {isEmptyObject(agent.permissionPolicy) ? (
                  <p className="text-[13px] text-muted-foreground">{t('Default')}</p>
                ) : (
                  <JsonBlock value={agent.permissionPolicy} maxHeight="max-h-48" />
                )}
              </div>
            </div>
          </Section>
        </div>
      </div>

      <TechDetails className="mt-6">
        <JsonBlock value={agent} maxHeight="max-h-[32rem]" />
      </TechDetails>
    </Page>
  );
}
